/**
 * React Query hooks for Analytics Dashboard System
 * Wraps AnalyticsService with React Query caching and state management
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrganization } from "@/contexts/OrganizationContext";
import { createAnalyticsService } from "@/api/services/analytics.service";
import type {

  AnalyticsDashboardCreateDto,
  AnalyticsDashboardUpdateDto,
  AnalyticsDashboardScopeFilterDto,

  AnalyticsGraphCreateDto,
  AnalyticsGraphUpdateDto,
} from "@/api/types/dtos/analytics/AnalyticsDto";

// Query keys factory
export const analyticsKeys = {
  all: (orgId: string) => ["analytics", "dashboards", orgId] as const,
  lists: (orgId: string) => ["analytics", "dashboards", orgId, "list"] as const,
  details: (orgId: string, id: string) => ["analytics", "dashboards", orgId, "detail", id] as const,
  data: (orgId: string, dashboardId: string) => ["analytics", "data", orgId, dashboardId] as const,
  drillDown: (orgId: string, graphId: string, filterValue: string) =>
    ["analytics", "drilldown", orgId, graphId, filterValue] as const,
  fields: (orgId: string, entity: string) => ["analytics", "fields", orgId, entity] as const,
  scopeOptions: (orgId: string, filterType: string) =>
    ["analytics", "scopeOptions", orgId, filterType] as const,
  programHierarchy: (orgId: string) => ["analytics", "programHierarchy", orgId] as const,
};

// ==================== Dashboard Hooks ====================

/**
 * Fetch all dashboards for the current organization
 */
export const useAnalyticsDashboards = () => {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: analyticsKeys.lists(selectedOrganization?.id || "pending"),
    queryFn: () => {
      if (!selectedOrganization?.id) {
        return Promise.resolve([]);
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.findAllDashboards().then((res) => res.data);
    },
    enabled: !!selectedOrganization?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Fetch a single dashboard by ID
 */
export const useAnalyticsDashboard = (dashboardId: string) => {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: analyticsKeys.details(selectedOrganization?.id || "pending", dashboardId),
    queryFn: () => {
      if (!selectedOrganization?.id) {
        return Promise.resolve(null);
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.findDashboardById(dashboardId).then((res) => res.data);
    },
    enabled: !!selectedOrganization?.id && !!dashboardId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Fetch dashboard with all graph data
 */
export const useDashboardGraphData = (dashboardId: string) => {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: analyticsKeys.data(selectedOrganization?.id || "pending", dashboardId),
    queryFn: () => {
      if (!selectedOrganization?.id) {
        return Promise.resolve(null);
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.getDashboardGraphData(dashboardId).then((res) => res.data);
    },
    enabled: !!selectedOrganization?.id && !!dashboardId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// ==================== Dashboard Mutations ====================

/**
 * Create a new dashboard
 */
export const useCreateDashboard = () => {
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: AnalyticsDashboardCreateDto) => {
      if (!selectedOrganization?.id) {
        return Promise.reject(new Error("Organization ID is required"));
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.createDashboard(dto).then((res) => res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.lists(selectedOrganization?.id || "pending"),
      });
    },
  });
};

/**
 * Update a dashboard
 */
export const useUpdateDashboard = () => {
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dashboardId,
      dto,
    }: {
      dashboardId: string;
      dto: AnalyticsDashboardUpdateDto;
    }) => {
      if (!selectedOrganization?.id) {
        return Promise.reject(new Error("Organization ID is required"));
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.updateDashboard(dashboardId, dto).then((res) => res.data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.details(
          selectedOrganization?.id || "pending",
          variables.dashboardId
        ),
      });
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.lists(selectedOrganization?.id || "pending"),
      });
    },
  });
};

/**
 * Delete a dashboard
 */
export const useDeleteDashboard = () => {
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dashboardId: string) => {
      if (!selectedOrganization?.id) {
        return Promise.reject(new Error("Organization ID is required"));
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.deleteDashboard(dashboardId).then((res) => res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.lists(selectedOrganization?.id || "pending"),
      });
    },
  });
};

/**
 * Duplicate a dashboard
 */
export const useDuplicateDashboard = () => {
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dashboardId: string) => {
      if (!selectedOrganization?.id) {
        return Promise.reject(new Error("Organization ID is required"));
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.duplicateDashboard(dashboardId).then((res) => res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.lists(selectedOrganization?.id || "pending"),
      });
    },
  });
};

// ==================== Scope Filter Mutation ====================

/**
 * Update dashboard scope filters
 */
export const useUpdateScopeFilters = () => {
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dashboardId,
      scopeFilters,
    }: {
      dashboardId: string;
      scopeFilters: AnalyticsDashboardScopeFilterDto[];
    }) => {
      if (!selectedOrganization?.id) {
        return Promise.reject(new Error("Organization ID is required"));
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.updateScopeFilters(dashboardId, scopeFilters).then((res) => res.data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.details(
          selectedOrganization?.id || "pending",
          variables.dashboardId
        ),
      });
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.data(selectedOrganization?.id || "pending", variables.dashboardId),
      });
    },
  });
};

// ==================== Graph Mutations ====================

/**
 * Add a graph to a dashboard
 */
export const useAddGraph = () => {
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dashboardId, dto }: { dashboardId: string; dto: AnalyticsGraphCreateDto }) => {
      if (!selectedOrganization?.id) {
        return Promise.reject(new Error("Organization ID is required"));
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.addGraph(dashboardId, dto).then((res) => res.data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.details(
          selectedOrganization?.id || "pending",
          variables.dashboardId
        ),
      });
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.data(selectedOrganization?.id || "pending", variables.dashboardId),
      });
    },
  });
};

/**
 * Update a graph
 */
export const useUpdateGraph = () => {
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dashboardId,
      graphId,
      dto,
    }: {
      dashboardId: string;
      graphId: string;
      dto: AnalyticsGraphUpdateDto;
    }) => {
      if (!selectedOrganization?.id) {
        return Promise.reject(new Error("Organization ID is required"));
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.updateGraph(dashboardId, graphId, dto).then((res) => res.data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.details(
          selectedOrganization?.id || "pending",
          variables.dashboardId
        ),
      });
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.data(selectedOrganization?.id || "pending", variables.dashboardId),
      });
    },
  });
};

/**
 * Delete a graph
 */
export const useDeleteGraph = () => {
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dashboardId, graphId }: { dashboardId: string; graphId: string }) => {
      if (!selectedOrganization?.id) {
        return Promise.reject(new Error("Organization ID is required"));
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.deleteGraph(dashboardId, graphId).then((res) => res.data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.details(
          selectedOrganization?.id || "pending",
          variables.dashboardId
        ),
      });
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.data(selectedOrganization?.id || "pending", variables.dashboardId),
      });
    },
  });
};

/**
 * Duplicate a graph
 */
export const useDuplicateGraph = () => {
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dashboardId, graphId }: { dashboardId: string; graphId: string }) => {
      if (!selectedOrganization?.id) {
        return Promise.reject(new Error("Organization ID is required"));
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.duplicateGraph(dashboardId, graphId).then((res) => res.data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.details(
          selectedOrganization?.id || "pending",
          variables.dashboardId
        ),
      });
      queryClient.invalidateQueries({
        queryKey: analyticsKeys.data(selectedOrganization?.id || "pending", variables.dashboardId),
      });
    },
  });
};

// ==================== Drill-down Hook ====================

/**
 * Get drill-down data for a graph
 */
export const useDrillDownData = (
  graphId: string,
  filterValue: string,
  options?: { enabled?: boolean }
) => {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: analyticsKeys.drillDown(selectedOrganization?.id || "pending", graphId, filterValue),
    queryFn: () => {
      if (!selectedOrganization?.id) {
        return Promise.resolve(null);
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.getDrillDownData(graphId, filterValue).then((res) => res.data);
    },
    enabled: options?.enabled !== false && !!selectedOrganization?.id && !!graphId && !!filterValue,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  });
};

// ==================== Metadata Hooks ====================

/**
 * Get available fields for a data source entity
 */
export const useAvailableFields = (dataSourceEntity: string) => {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: analyticsKeys.fields(selectedOrganization?.id || "pending", dataSourceEntity),
    queryFn: () => {
      if (!selectedOrganization?.id) {
        return Promise.resolve([]);
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.getAvailableFields(dataSourceEntity).then((res) => res.data);
    },
    enabled: !!selectedOrganization?.id && !!dataSourceEntity,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Get scope filter options
 */
export const useScopeFilterOptions = (filterType: string) => {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: analyticsKeys.scopeOptions(selectedOrganization?.id || "pending", filterType),
    queryFn: () => {
      if (!selectedOrganization?.id) {
        return Promise.resolve([]);
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.getScopeFilterOptions(filterType).then((res) => res.data);
    },
    enabled: !!selectedOrganization?.id && !!filterType,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get program hierarchy for CHALLENGE_FORM field selection
 * Returns programs with their versions, levels, and items for hierarchical navigation
 */
export const useProgramHierarchy = () => {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: analyticsKeys.programHierarchy(selectedOrganization?.id || "pending"),
    queryFn: () => {
      if (!selectedOrganization?.id) {
        return Promise.resolve([]);
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.getProgramHierarchy().then((res) => res.data);
    },
    enabled: !!selectedOrganization?.id,
    staleTime: 15 * 60 * 1000, // 15 minutes - hierarchy doesn't change often
    refetchOnWindowFocus: false,
  });
};

/**
 * Get the full data source catalog (all sources, fields, labels, reachability).
 * This is the single source of truth for the graph editor UI.
 */
export const useFieldsCatalog = () => {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: ["analytics", "fieldsCatalog", selectedOrganization?.id || "pending"],
    queryFn: () => {
      if (!selectedOrganization?.id) {
        return Promise.resolve([]);
      }
      const service = createAnalyticsService(selectedOrganization.id);
      return service.getFieldsCatalog().then((res) => res.data);
    },
    enabled: !!selectedOrganization?.id,
    staleTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
  });
};

// ==================== Combined Dashboard Hook ====================

/**
 * Complete dashboard hook - fetches dashboard and graph data together
 * Note: This hook is designed for use in components that need both dashboard metadata and graph data
 */
export const useAnalyticsDashboardWithData = (dashboardId: string) => {
  const dashboardQuery = useAnalyticsDashboard(dashboardId);
  const graphDataQuery = useDashboardGraphData(dashboardId);

  return {
    dashboard: dashboardQuery.data,
    graphData: graphDataQuery.data,
    isLoading: dashboardQuery.isLoading || graphDataQuery.isLoading,
    error: dashboardQuery.error || graphDataQuery.error,
    refetch: () => {
      dashboardQuery.refetch();
      graphDataQuery.refetch();
    },
  };
};
